import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Loader2, MessageCircle } from "lucide-react";
import Reveal from "@/components/landing/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initialForm = { name: "", email: "", phone: "", message: "" };

export default function ContactForm() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      await axios.post("https://services.leadconnectorhq.com/hooks/sPemcCUt2EMV7wgo9q3q/webhook-trigger/GRZ0XAWdoVjAWJenHllK", form);
      toast.success("¡Solicitud enviada! Te contactaremos pronto.");
      setForm(initialForm);
    } catch (err) {
      toast.error("No pudimos enviar tu solicitud. Inténtalo de nuevo o escríbenos por WhatsApp.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contacto" className="py-20 lg:py-32 bg-slate-50" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 lg:gap-16 items-start">
        <Reveal>
          <span className="text-sm font-semibold uppercase tracking-wider text-blue-600">Solicita más información</span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight text-slate-900">
            Déjanos tus datos y te contactaremos pronto
          </h2>
          <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed max-w-lg">
            Un asesor del programa de inglés te brindará toda la información sobre niveles,
            horarios de las clases presenciales y promociones vigentes.
          </p>
          <a
            href="https://wa.me/573185329505?text=Hola%2C%20quiero%20m%C3%A1s%20informaci%C3%B3n%20sobre%20el%20programa%20de%20ingl%C3%A9s"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-whatsapp-link"
            className="mt-8 inline-flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 hover:shadow-lg hover:shadow-blue-900/5 transition-shadow"
          >
            <span className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center">
              <MessageCircle className="w-5 h-5" />
            </span>
            <span>
              <span className="block text-xs text-slate-500">¿Necesitas ayuda?</span>
              <span className="block text-sm font-semibold text-slate-900">Chatea con nosotros por WhatsApp</span>
            </span>
          </a>
        </Reveal>

        <Reveal delay={0.1}>
          <form
            onSubmit={handleSubmit}
            data-testid="lead-form"
            className="rounded-2xl border border-slate-200 bg-white p-7 sm:p-9 shadow-sm space-y-5"
          >
            <div>
              <label htmlFor="lead-name" className="block text-sm font-medium text-slate-700 mb-1.5">
                Nombre completo
              </label>
              <input
                id="lead-name"
                type="text"
                required
                minLength={2}
                value={form.name}
                onChange={update("name")}
                data-testid="lead-form-name-input"
                placeholder="Tu nombre"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition"
              />
            </div>
            <div>
              <label htmlFor="lead-email" className="block text-sm font-medium text-slate-700 mb-1.5">
                Correo electrónico
              </label>
              <input
                id="lead-email"
                type="email"
                required
                value={form.email}
                onChange={update("email")}
                data-testid="lead-form-email-input"
                placeholder="tucorreo@ejemplo.com"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition"
              />
            </div>
            <div>
              <label htmlFor="lead-phone" className="block text-sm font-medium text-slate-700 mb-1.5">
                Teléfono / WhatsApp
              </label>
              <input
                id="lead-phone"
                type="tel"
                required
                minLength={7}
                value={form.phone}
                onChange={update("phone")}
                data-testid="lead-form-phone-input"
                placeholder="300 123 4567"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition"
              />
            </div>
            <div>
              <label htmlFor="lead-message" className="block text-sm font-medium text-slate-700 mb-1.5">
                Mensaje <span className="text-slate-400 font-normal">(opcional)</span>
              </label>
              <textarea
                id="lead-message"
                rows={4}
                value={form.message}
                onChange={update("message")}
                data-testid="lead-form-message-input"
                placeholder="Cuéntanos qué te gustaría saber del programa"
                className="w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-600/30 focus:border-blue-600 transition resize-none"
              />
            </div>
            <button
              type="submit"
              disabled={sending}
              data-testid="lead-form-submit-button"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-blue-600 px-7 py-3.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              {sending ? "Enviando..." : "Quiero recibir información"}
            </button>
          </form>
        </Reveal>
      </div>
    </section>
  );
}
